import { Badge, Button, Checkbox, Group, Paper, ScrollArea, Skeleton, Table, Text, TextInput, Tooltip } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDeviceFloppy, IconSearch } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";

import { http } from "../../api/http";
import { useT } from "../../i18n/script";

interface Role { name: string; description: string; permissions: string[] }
interface L { id: number; code: string; title: string; group_name: string; kind: string; status: string; origin: string;
  required_permission: string; allowed_roles: string[]; is_basemap: boolean; service_name: string | null }

const ROLE_SHORT: Record<string, string> = {
  administrator: "Admin", rukovodilac: "Rukovodilac", agronom: "Agronom", opstinski_referent: "Referent",
  analiticar: "Analitičar", proizvodjac: "Proizvođač", registrovani_korisnik: "Registrovani", javni_korisnik: "Javni",
  eksterni_korisnik: "Eksterni",
};

/** Svi slojevi × vrste korisnika (role): čekiranjem administrator određuje ko vidi koji sloj. */
export default function LayerVisibilityMatrix() {
  const { t } = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["layer-visibility"], queryFn: () => http.get<{ roles: Role[]; layers: L[] }>("/api/v1/layers/visibility") });
  const [draft, setDraft] = useState<Record<number, string[]>>({});
  const [f, setF] = useState("");
  const [busy, setBusy] = useState(false);
  const ORDER = Object.keys(ROLE_SHORT);
  const roles = [...(q.data?.roles ?? [])].sort((a, b) => (ORDER.indexOf(a.name) + 1 || 99) - (ORDER.indexOf(b.name) + 1 || 99));
  const allNames = roles.map((r) => r.name);
  const current = (l: L) => draft[l.id] ?? (l.allowed_roles.length ? l.allowed_roles : allNames);
  const layers = useMemo(() => (q.data?.layers ?? []).filter((l) => !f || `${l.title} ${l.group_name}`.toLowerCase().includes(f.toLowerCase())),
    [q.data, f]);
  const groups = useMemo(() => [...new Set(layers.map((l) => (l.is_basemap ? "Podloge" : l.group_name)))], [layers]);
  const dirty = Object.keys(draft).length;

  function toggle(l: L, role: string) {
    const cur = new Set(current(l));
    cur.has(role) ? cur.delete(role) : cur.add(role);
    setDraft((d) => ({ ...d, [l.id]: [...cur] }));
  }
  function column(role: string, on: boolean) {
    const next = { ...draft };
    for (const l of layers) {
      const cur = new Set(current(l));
      on ? cur.add(role) : cur.delete(role);
      next[l.id] = [...cur];
    }
    setDraft(next);
  }
  async function save() {
    setBusy(true);
    try {
      const changes = Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, v.length === allNames.length ? [] : v]));
      await http.put("/api/v1/layers/visibility", { changes });
      notifications.show({ color: "green", message: t("Vidljivost slojeva je sačuvana — važi od sljedećeg otvaranja mape.") });
      setDraft({});
      qc.invalidateQueries({ queryKey: ["layer-visibility"] });
      qc.invalidateQueries({ queryKey: ["layers"] });
      qc.invalidateQueries({ queryKey: ["layers-manage"] });
    } catch (e) {
      notifications.show({ color: "red", message: String((e as Error).message) });
    } finally { setBusy(false); }
  }

  if (q.isLoading) return <Skeleton h={420} radius="lg" />;
  return (
    <Paper withBorder radius="lg" p="md">
      <Group justify="space-between" mb="sm" wrap="wrap">
        <div>
          <Text fw={750}>{t("Ko vidi koji sloj")}</Text>
          <Text size="xs" c="dimmed">{t("Čekirajte vrste korisnika (role) koje vide sloj na mapi. Siva kućica: rola nema osnovno ovlašćenje sloja (npr. pregled voćnjaka), pa ga ne može vidjeti ni kad je čekirana. Klik na naziv role čekira/odčekira cijelu kolonu.")}</Text>
        </div>
        <Group gap="xs">
          <TextInput size="xs" leftSection={<IconSearch size={14} />} placeholder={t("Pretraga slojeva")} value={f} onChange={(e) => setF(e.currentTarget.value)} />
          {dirty > 0 && <Button size="xs" variant="default" onClick={() => setDraft({})}>{t("Poništi")}</Button>}
          <Button size="xs" leftSection={<IconDeviceFloppy size={14} />} disabled={!dirty} loading={busy} onClick={save}>
            {dirty ? t(`Sačuvaj (${dirty})`) : t("Sačuvaj")}
          </Button>
        </Group>
      </Group>
      <ScrollArea type="auto">
        <Table striped highlightOnHover withTableBorder verticalSpacing={6} fz="sm" miw={760}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ minWidth: 260 }}>{t("Sloj")}</Table.Th>
              {roles.map((r) => {
                const all = layers.every((l) => current(l).includes(r.name));
                return (
                  <Table.Th key={r.name} ta="center" style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => column(r.name, !all)}>
                    <Tooltip label={r.description} withArrow><span>{t(ROLE_SHORT[r.name] ?? r.name)}</span></Tooltip>
                  </Table.Th>
                );
              })}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {groups.map((g) => (
              <Fragment key={g}>
                <Table.Tr><Table.Td colSpan={roles.length + 1} bg="var(--mantine-color-agri-light)">
                  <Text size="xs" fw={800} tt="uppercase" c="agri.8">{t(g)}</Text></Table.Td></Table.Tr>
                {layers.filter((l) => (l.is_basemap ? "Podloge" : l.group_name) === g).map((l) => (
                  <Table.Tr key={l.id} style={draft[l.id] ? { boxShadow: "inset 3px 0 0 var(--mantine-color-orange-5)" } : undefined}>
                    <Table.Td>
                      <Text size="sm" fw={600}>{l.title}</Text>
                      <Group gap={4}>
                        <Badge size="xs" variant="light">{l.kind.toUpperCase()}</Badge>
                        {l.origin !== "sistem" && <Badge size="xs" variant="light" color="grape">{l.service_name ?? t("fajl")}</Badge>}
                        {l.status === "iskljucen" && <Badge size="xs" color="gray">{t("isključen")}</Badge>}
                      </Group>
                    </Table.Td>
                    {roles.map((r) => {
                      const allowedByPerm = r.permissions.includes(l.required_permission) || r.name === "administrator";
                      return (
                        <Table.Td key={r.name} ta="center">
                          <Tooltip label={allowedByPerm ? "" : t(`Rola nema ovlašćenje ${l.required_permission}`)} disabled={allowedByPerm} withArrow>
                            <span style={{ display: "inline-block" }}>
                              <Checkbox checked={current(l).includes(r.name)} onChange={() => toggle(l, r.name)} color={allowedByPerm ? "agri" : "gray"}
                                style={{ opacity: allowedByPerm ? 1 : 0.45, display: "inline-block" }} aria-label={`${l.title} — ${r.name}`} />
                            </span>
                          </Tooltip>
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                ))}
              </Fragment>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Paper>
  );
}
